import { useCallback, useEffect, useState } from "react";

// Google Programmable Search Engine (CSE) loader.
// Loads the CSE script exactly once, tracks readiness, and exposes a
// helper to run searches against a manually-rendered "searchresults-only"
// element so we can drive it from our own branded search box.

let scriptState = "idle"; // idle | loading | ready | error
const listeners = new Set();
const RESULTS_DIV_ID = "caprio-results";

// Track which DOM node the current CSE element is bound to. The results
// container is unmounted when the user returns home, so on the next search
// we must re-render a fresh element (new gname) into the new node.
let currentGname = null;
let currentNode = null;
let gnameCounter = 0;

function notify(next) {
    scriptState = next;
    listeners.forEach((l) => l(next));
}

function loadCse(cx) {
    if (scriptState === "loading" || scriptState === "ready") return;
    if (window.google && window.google.search && window.google.search.cse) {
        notify("ready");
        return;
    }
    notify("loading");
    window.__gcse = {
        parsetags: "explicit", // we render elements manually to avoid duplicates
        callback: () => notify("ready"),
    };
    const existing = document.getElementById("ck-cse-script");
    if (existing) return;
    const s = document.createElement("script");
    s.id = "ck-cse-script";
    s.async = true;
    s.src = `https://cse.google.com/cse.js?cx=${cx}`;
    s.onerror = () => notify("error");
    document.head.appendChild(s);
}

export function useGoogleCse(cx) {
    const [status, setStatus] = useState(scriptState);

    useEffect(() => {
        const l = (s) => setStatus(s);
        listeners.add(l);
        loadCse(cx);
        setStatus(scriptState);
        return () => listeners.delete(l);
    }, [cx]);

    const executeSearch = useCallback((query) => {
        const cse = window.google?.search?.cse?.element;
        if (!cse) return false;
        const node = document.getElementById(RESULTS_DIV_ID);
        if (!node) return false;

        // Re-render a fresh element if the target node changed (remount) or
        // the current element is gone.
        const needsRender =
            node !== currentNode ||
            !currentGname ||
            !cse.getElement(currentGname);
        if (needsRender) {
            currentGname = `caprio_${++gnameCounter}`;
            currentNode = node;
            cse.render({
                div: RESULTS_DIV_ID,
                tag: "searchresults-only",
                gname: currentGname,
            });
        }

        const el = cse.getElement(currentGname);
        if (el) {
            el.execute(query);
            return true;
        }
        return false;
    }, []);

    const retry = useCallback(() => {
        scriptState = "idle";
        const s = document.getElementById("ck-cse-script");
        if (s) s.remove();
        loadCse(cx);
    }, [cx]);

    return { status, executeSearch, retry, RESULTS_DIV_ID };
}
